'use client'

import {
  FullBleedImage,
  OfferBadge,
  OfferFrame,
  SafeText,
  clampIndex,
  getDesign,
  getOfferCopy,
  getOfferVisual,
  type OfferPackProps,
} from '../shared/OfferPackCore'

const bg = [
  'https://images.unsplash.com/photo-1517841905240-472988babdf9?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1543269865-cbf427effbad?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1497366811353-6870744d04b2?q=80&w=1400&auto=format&fit=crop',
]

const total = 5
const serif = '"Georgia", "Times New Roman", serif'
const sans = '"Inter", "Helvetica Neue", Arial, sans-serif'

export default function CreatorOfferConversionPack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
}: OfferPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getDesign(draft, '#B83A2E')
  const accent = design.accent
  const paper = '#ECE7DB'
  const ink = '#111111'
  const muted = 'rgba(17,17,17,0.58)'

  const copy = getOfferCopy(draft, index, {
    eyebrow: 'Personal brand offer',
    headline: 'Construye una marca que venda sin perseguir',
    subheadline: 'Una propuesta clara para convertir tu experiencia en confianza, contenido y ventas.',
    offer: 'Sistema editorial + oferta clara',
    proof: 'Más claridad. Más autoridad. Más intención.',
    urgency: 'Cupo limitado esta semana',
    cta: 'Quiero mi lugar',
    bullets: ['Mensaje central', 'Contenido estratégico', 'Oferta lista para vender'],
  })

  return (
    <OfferFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      text={index === 0 ? '#ffffff' : ink}
      className="bg-[#ECE7DB]"
    >
      {index === 0 ? (
        <>
          <FullBleedImage
            {...getOfferVisual(draft, index, bg[0], {
              overlay: 'black',
              opacity: 0.26,
            })}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/76 via-black/24 to-transparent" />
          <div className="absolute left-8 top-8 z-10">
            <OfferBadge color="#fff">{copy.eyebrow}</OfferBadge>
          </div>
          <div className="absolute bottom-16 left-8 z-10 max-w-[76%] text-white">
            <SafeText className="text-[clamp(42px,10vw,70px)] font-normal leading-[0.88] tracking-[-0.055em]" style={{ fontFamily: serif }}>
              {copy.headline}
            </SafeText>
            <SafeText className="mt-5 max-w-[76%] text-[15px] leading-[1.42] text-white/78" style={{ fontFamily: sans }}>
              {copy.subheadline}
            </SafeText>
          </div>
          <div className="absolute bottom-8 right-8 text-[11px] font-black uppercase tracking-[0.18em] text-white" style={{ fontFamily: sans }}>
            Offer / 01
          </div>
        </>
      ) : null}

      {index === 1 ? (
        <>
          <div className="absolute inset-0" style={{ background: paper }} />
          <div className="absolute left-8 top-8 text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: accent, fontFamily: sans }}>
            Qué vas a construir
          </div>
          <div className="absolute left-8 top-20 max-w-[54%]" style={{ color: ink }}>
            <SafeText className="text-[50px] font-normal leading-[0.9] tracking-[-0.05em]" style={{ fontFamily: serif }}>
              {copy.offer || 'Una oferta con punto de vista propio.'}
            </SafeText>
            <SafeText className="mt-6 max-w-[86%] text-[15px] leading-[1.5]" style={{ color: muted, fontFamily: sans }}>
              {copy.body || 'No se trata de publicar más. Se trata de que cada publicación sostenga una idea, una promesa y una invitación clara.'}
            </SafeText>
          </div>
          <div className="absolute bottom-0 right-0 h-[66%] w-[38%] overflow-hidden rounded-tl-[120px]">
            <FullBleedImage {...getOfferVisual(draft, index, bg[1], { overlay: 'black', opacity: 0.05 })} />
          </div>
          <div className="absolute bottom-8 left-8 h-px w-[52%]" style={{ background: 'rgba(17,17,17,0.16)' }} />
        </>
      ) : null}

      {index === 2 ? (
        <>
          <div className="absolute inset-0" style={{ background: '#F6F0E5' }} />
          <div className="absolute inset-x-8 top-8 flex items-center justify-between border-b pb-5" style={{ borderColor: 'rgba(17,17,17,0.14)', fontFamily: sans }}>
            <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: accent }}>Método</div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: muted }}>3 piezas</div>
          </div>
          <div className="absolute left-8 right-8 top-24 grid grid-cols-3 gap-4">
            {(copy.bullets.length ? copy.bullets : ['Mensaje', 'Contenido', 'Conversión']).slice(0, 3).map((item, idx) => (
              <div key={idx} className="min-h-[255px] border-t pt-5" style={{ borderColor: idx === 0 ? accent : 'rgba(17,17,17,0.18)' }}>
                <div className="text-[54px] font-normal leading-none" style={{ color: idx === 0 ? accent : ink, fontFamily: serif }}>
                  0{idx + 1}
                </div>
                <SafeText className="mt-7 text-[22px] leading-[1.02] tracking-[-0.03em]" style={{ color: ink, fontFamily: serif }}>
                  {item}
                </SafeText>
                <div className="mt-5 text-[12px] leading-[1.45]" style={{ color: muted, fontFamily: sans }}>
                  Una pieza clara dentro del sistema para vender con más intención.
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {index === 3 ? (
        <>
          <div className="absolute inset-0" style={{ background: ink }} />
          <div className="absolute right-0 top-0 h-full w-[48%] opacity-80">
            <FullBleedImage {...getOfferVisual(draft, index, bg[3], { overlay: 'black', opacity: 0.28, blur: true })} />
          </div>
          <div className="absolute left-8 top-10 max-w-[54%] text-white">
            <div className="text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: accent, fontFamily: sans }}>
              Prueba de claridad
            </div>
            <SafeText className="mt-5 text-[48px] font-normal leading-[0.92] tracking-[-0.05em]" style={{ fontFamily: serif }}>
              {copy.proof || 'Cuando el mensaje es claro, la venta deja de sentirse forzada.'}
            </SafeText>
            <SafeText className="mt-7 max-w-[82%] text-[15px] leading-[1.5] text-white/66" style={{ fontFamily: sans }}>
              {copy.subheadline}
            </SafeText>
          </div>
          <div className="absolute bottom-9 left-8 h-[2px] w-32" style={{ background: accent }} />
        </>
      ) : null}

      {index === 4 ? (
        <>
          <div className="absolute inset-0" style={{ background: paper }} />
          <div className="absolute inset-7 border" style={{ borderColor: 'rgba(17,17,17,0.16)' }} />
          <div className="absolute left-10 top-10 max-w-[70%]" style={{ color: ink }}>
            <div className="text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: accent, fontFamily: sans }}>
              Invitación
            </div>
            <SafeText className="mt-6 text-[58px] font-normal leading-[0.86] tracking-[-0.06em]" style={{ fontFamily: serif }}>
              {copy.cta || 'Da el siguiente paso'}
            </SafeText>
          </div>
          <div className="absolute bottom-10 left-10 max-w-[46%] text-[15px] leading-[1.45]" style={{ color: muted, fontFamily: sans }}>
            {copy.urgency || 'Disponible para quienes quieren convertir su experiencia en una marca más clara y rentable.'}
          </div>
          <div className="absolute bottom-10 right-10 rounded-full px-6 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-white" style={{ background: accent, fontFamily: sans }}>
            Empezar
          </div>
        </>
      ) : null}
    </OfferFrame>
  )
}
